import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, Plus, Loader2, Database, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export function UsdaSearchDialog({ open, onClose, API, isDark, existingFoodNames, onAddFood }) {
  const [usdaQuery, setUsdaQuery] = useState("");
  const [usdaResults, setUsdaResults] = useState([]);
  const [usdaSearching, setUsdaSearching] = useState(false);

  const searchUsda = async () => {
    if (!usdaQuery.trim()) return;
    setUsdaSearching(true);
    try {
      const { data } = await axios.post(`${API}/admin/foods/usda-search`, { query: usdaQuery, page_size: 12 });
      const nameSet = new Set(existingFoodNames.map(n => n.toLowerCase().trim()));
      const filtered = (data.results || []).filter(r => !nameSet.has(r.description.toLowerCase().trim()));
      setUsdaResults(filtered);
      if (filtered.length === 0 && (data.results || []).length > 0) {
        toast.info("Всички намерени храни вече са добавени в базата данни");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "USDA грешка"); }
    finally { setUsdaSearching(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[85vh] flex flex-col ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white'}`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            <Zap className="w-5 h-5 text-[#0EA5E9]" /> USDA FoodData Central
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <div className="flex gap-2 shrink-0">
            <Input placeholder="Търсене в USDA (на английски)..."
              value={usdaQuery} onChange={e => setUsdaQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && searchUsda()}
              className={isDark ? 'bg-gray-800 border-gray-600 text-white placeholder:text-gray-500' : ''}
              data-testid="usda-query-input" />
            <Button onClick={searchUsda} disabled={usdaSearching} data-testid="usda-search-go-btn"
              className={isDark ? 'bg-[#0EA5E9] hover:bg-[#0EA5E9]/80 text-white' : ''}>
              {usdaSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0" style={{ maxHeight: '55vh' }}>
            {usdaResults.length > 0 ? (
              <div className="space-y-2">
                {usdaResults.map((r) => (
                  <div key={r.fdcId || r.description} className={`p-3 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}
                    data-testid={`usda-result-${r.fdcId || r.description}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
                        {r.description}
                      </span>
                      <Button size="sm" variant="outline" onClick={() => onAddFood(r)}
                        className={isDark ? 'border-gray-600 text-white hover:bg-gray-700' : ''}>
                        <Plus className="w-3 h-3 mr-1" /> Добави
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-[11px]">
                      <div><span className="font-mono text-[#10B981] font-bold">DIL: {r.dil_100g}</span></div>
                      <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>DII%: {r.dii_percent}</div>
                      <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>{r.calories_100g} kcal</div>
                      <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>P:{r.protein_100g} C:{r.carbs_100g} F:{r.fat_100g}</div>
                    </div>
                    <div className={`grid grid-cols-4 gap-2 text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <span>20g: {r.dil_20g}</span>
                      <span>30g: {r.dil_30g}</span>
                      <span>40g: {r.dil_40g}</span>
                      <span>50g: {r.dil_50g}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <Database className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Търсете храна в USDA базата</p>
                <p className="text-xs mt-1">Напр: chicken breast, salmon, broccoli</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
